import { toast } from '../components/notifications';

// Fehlermeldungen für verschiedene Fehlertypen
const ERROR_MESSAGES = {
  VALIDATION_ERROR: 'Bitte überprüfe deine Eingaben.',
  AUTHENTICATION_ERROR: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
  AUTHORIZATION_ERROR: 'Du hast keine Berechtigung für diese Aktion.',
  NOT_FOUND_ERROR: 'Die angeforderte Resource wurde nicht gefunden.',
  DATABASE_ERROR: 'Es ist ein Datenbankfehler aufgetreten.',
  TENANT_ERROR: 'Fehler bei der Schulzuordnung.',
  SERVER_ERROR: 'Ein unerwarteter Fehler ist aufgetreten.',
  NETWORK_ERROR: 'Keine Verbindung. Deine Änderung wurde nicht gespeichert.',
  SESSION_ERROR: 'Es gab ein Problem mit deiner Lernsession.',
};

// Lesende Requests (GET) melden Netzwerkausfaelle nicht per Toast: Beim Seitenaufbau
// laufen mehrere davon parallel, und der Offline-Banner (NetworkStatus) bzw. die
// leere Ansicht zeigen den Zustand bereits. Nur wenn eine Aenderung des Users
// verloren geht (POST/PUT/PATCH/DELETE), lohnt eine Meldung.
const isReadRequest = (error) => {
  const method = error?.config?.method;
  return typeof method === 'string' && method.toLowerCase() === 'get';
};

// Toast-Konfigurationen für verschiedene Fehlertypen
const TOAST_CONFIGS = {
  VALIDATION_ERROR: { type: 'warning', autoClose: 4000 },
  AUTHENTICATION_ERROR: { type: 'error', autoClose: 3000 },
  AUTHORIZATION_ERROR: { type: 'error', autoClose: 4000 },
  NOT_FOUND_ERROR: { type: 'warning', autoClose: 4000 },
  DATABASE_ERROR: { type: 'error', autoClose: 4000 },
  TENANT_ERROR: { type: 'error', autoClose: 4000 },
  SERVER_ERROR: { type: 'error', autoClose: 4000 },
  NETWORK_ERROR: { type: 'error', autoClose: 4000 },
  SESSION_ERROR: { type: 'error', autoClose: 4000 },
};

class ErrorHandler {
  static handle(error) {
    // Extrahiere Fehlerdetails aus der API-Antwort.
    // Viele Routen (u.a. alle Session-Routen) liefern `error` als reinen String
    // statt als Objekt. Ohne diesen Zweig war errorData.type undefined, der
    // Handler fiel auf SERVER_ERROR zurueck und zeigte "Ein unerwarteter Fehler
    // ist aufgetreten." - ausgerechnet ueber der praezisen Meldung, die daneben
    // schon inline stand.
    const rawError = error.response?.data?.error;
    let errorData;
    if (typeof rawError === 'string' && rawError.trim()) {
      errorData = { type: 'VALIDATION_ERROR', message: rawError, useMessageOnly: true };
    } else if (rawError && typeof rawError === 'object') {
      errorData = rawError;
    } else {
      // Bewusst OHNE error.message: Das waere die rohe Axios-Meldung
      // ("timeout of 10000ms exceeded", "Request failed with status code 500") -
      // Technik, die im Toast niemandem hilft.
      errorData = { type: 'SERVER_ERROR' };
    }

    // Netzwerkfehler speziell behandeln
    if (!error.response) {
      errorData.type = 'NETWORK_ERROR';
      if (isReadRequest(error)) {
        return errorData;
      }
    }

    // Hole die Konfiguration für diesen Fehlertyp
    const config = TOAST_CONFIGS[errorData.type] || TOAST_CONFIGS.SERVER_ERROR;

    // Erstelle die Fehlermeldung
    const message = this.createErrorMessage(errorData);

    // Zeige Toast-Nachricht mit korrekter Methode.
    // toastId pro Fehlertyp: Die App feuert beim Seitenaufbau mehrere Requests
    // parallel. Faellt die Verbindung aus, schlagen alle gleichzeitig fehl und
    // jeder Aufruf zeigte bisher einen eigenen Toast - bei einem Timeout stapelten
    // sich so drei identische Meldungen. react-toastify unterdrueckt einen Toast,
    // dessen Id bereits aktiv ist; damit bleibt genau eine Meldung uebrig.
    //
    // `haptic` (Oktober 2026): toast.error vibriert selbst einmal. Ein
    // fehlgeschlagener Lese-Request (GET) im Hintergrund soll das nicht —
    // niemand hat gerade etwas getan, worauf der Impuls antworten würde.
    // Der Fortschrittsbalken bleibt aus wie bei allen Toasts (Vorgabe im Wrapper).
    const toastType = config.type || 'error'; // 'error', 'warning', 'info', 'success'
    toast[toastType](message, {
      toastId: `apierror-${errorData.type}`,
      haptic: !isReadRequest(error),
      position: "top-right",
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      autoClose: config.autoClose || 4000,
    });

    // Spezielle Behandlung für Authentifizierungsfehler
    if (errorData.type === 'AUTHENTICATION_ERROR') {
      // Redirect zur Login-Seite nach kurzer Verzögerung
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    }

    // Gib den Fehler für weitere Verarbeitung zurück
    return errorData;
  }

  static createErrorMessage(errorData) {
    // Liefert der Server bereits einen fertigen deutschen Satz, steht der allein.
    // Ein vorangestelltes "Bitte ueberpruefe deine Eingaben." waere hier falsch -
    // an einer Ablehnung wegen Rauswurf gibt es nichts zu ueberpruefen.
    if (errorData.useMessageOnly && errorData.message) {
      return errorData.message;
    }

    // Basis-Fehlermeldung
    let message = ERROR_MESSAGES[errorData.type] || ERROR_MESSAGES.SERVER_ERROR;

    // Füge spezifische Fehlermeldung hinzu, wenn vorhanden
    if (errorData.message && errorData.message !== message) {
      message += `\n${errorData.message}`;
    }

    // Füge Validierungsdetails hinzu, wenn vorhanden
    if (errorData.type === 'VALIDATION_ERROR' && errorData.details) {
      const details = Array.isArray(errorData.details)
        ? errorData.details.join('\n')
        : Object.values(errorData.details).join('\n');
      message += `\n${details}`;
    }

    return message;
  }

  // Hilfsmethode für Formularfehler
  static handleFormError(error, setErrors) {
    const errorData = this.handle(error);
    
    // Setze Formularfehler, wenn Validierungsfehler
    if (errorData.type === 'VALIDATION_ERROR' && errorData.details) {
      setErrors(
        Array.isArray(errorData.details)
          ? { _error: errorData.details.join(', ') }
          : errorData.details
      );
    }
  }

  // Hilfsmethode für Session-Fehler
  static handleSessionError(error, onSessionEnd) {
    const errorData = this.handle(error);
    
    // Bei Session-Fehlern zusätzliche Aktionen ausführen
    if (errorData.type === 'SESSION_ERROR') {
      onSessionEnd?.();
    }
  }
}

export default ErrorHandler; 